<script>
import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue';
import CollectionFrameOptions from '@/components/modals/Collection-frame-options.vue';
import NewFrameInCollection from '@/components/modals/New-frame-in-collection.vue';
import { message } from 'ant-design-vue';

const b = bem('page-collection');

export default {
  components: {
    LeftOutlined,
    RightOutlined,
    CollectionFrameOptions,
    NewFrameInCollection,
  },
  data() {
    return {
      selectedFrame: {},
      selectedOptions: [],
      isShowFramesModal: false,
      isShowFrameEditModal: false,
      selectedCompanyId: null,
      customUrl: '',
      seoTitle: '',
      seoDescription: '',
      seoKeywords: '',
    };
  },
  props: {
    selectedCollection: {
      type: Object,
      required: true,
    },
    goBack: {
      type: Function,
      required: true,
    },
    companyId: {
      type: [String, Number],
      required: true,
    },
  },
  methods: {
    ...mapActions({
      getCollectionDetailsAction: 'collections/getCollectionDetails',
      getTemplateCollectionsFramesAction: 'createTemplate/getTemplateCollectionsFrames',
      updateCollectionDetailsAction: 'collections/updateCollectionDetails',
      deleteFrameFromCollectionAction: 'collections/deleteFrameFromCollection',
      updateCollectionImageAction: 'collections/updateCollectionImage',
      updateFramesCollectionPositionsAction: 'collections/updateFramesCollectionPositions',
      getCompaniesAction: 'companies/getCompanies',
    }),
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    parseCustomInfo(customInfo) {
      if (!customInfo) return {};
      if (typeof customInfo === 'string') {
        try {
          return JSON.parse(customInfo);
        } catch {
          return {};
        }
      }
      return customInfo;
    },
    updateProductOrder(value) {
      this.updateFramesCollectionPositionsAction({
        changePosition: value,
        productCollectionId: this.selectedCollection.id,
      });
    },
    updateCollection() {
      const updateData = {
        name: this.collection.name,
        title: this.collection.title,
        description: this.collection.description,
      };

      const existingCustomInfo = this.parseCustomInfo(this.collection.customInfo);
      const updatedCustomInfo = {
        ...existingCustomInfo,
        url: this.customUrl || existingCustomInfo.url,
        seo: {
          ...existingCustomInfo.seo,
          seoTitle: this.seoTitle,
          seoDescription: this.seoDescription,
          seoKeywords: this.seoKeywords,
        },
      };
      updateData.customInfo = JSON.stringify(updatedCustomInfo);

      if (this.selectedCompanyId) {
        const selectedCompany = this.companies.find((c) => c.id === this.selectedCompanyId);
        updateData.companyId = this.selectedCompanyId;

        const pageKey = selectedCompany?.page_key || selectedCompany?.pageKey;
        if (pageKey) {
          updateData.pageKey = pageKey;
        }
      }

      this.updateCollectionDetailsAction({
        id: this.collection.id,
        value: updateData,
      });
    },
    selectFrame(value) {
      const newFrame = this.frames.find((item) => item.id === value.id) || {};
      const { options } = value;
      const newOptions = newFrame.options.map((item) => {
        const oldItem = options.find((itm) => item.id === itm.id);
        return oldItem || item;
      });
      this.selectedFrame = { ...value, options: newOptions };
      this.selectedOptions = value.options;
      this.isShowFrameEditModal = true;
    },
    deleteFrame(product) {
      const deletedOptions = product.options.map((opt) => opt.refProductId);
      this.deleteFrameFromCollectionAction({
        collectionId: this.collection.id,
        ids: [product.refProductId, ...deletedOptions],
      });
    },
    framesToggle() {
      this.isShowFramesModal = true;
    },
    framesClose() {
      this.isShowFramesModal = false;
    },
    frameEditToggle() {
      this.selectedFrame = {};
      this.selectedOptions = [];
      this.isShowFrameEditModal = false;
    },
    beforeUpload(file) {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG file!');
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must smaller than 2MB!');
      }
      return isJpgOrPng && isLt2M;
    },
    handleChangeImage(info) {
      this.updateCollectionImageAction({
        id: this.collection.id,
        file: info.file,
      });
    },
  },
  computed: {
    ...mapState({
      collection: (state) => state.collections.collection,
      frames: (state) => state.createTemplate.frames,
      companies: (state) => state.companies.companies,
    }),
    companiesOptions() {
      return this.companies.map((company) => ({
        label: company.displayName,
        value: company.id,
      }));
    },
  },
  async mounted() {
    await this.getCompaniesAction({ limit: 1000, offset: 0 });
    await this.getCollectionDetailsAction({ id: this.selectedCollection.id });
    this.getTemplateCollectionsFramesAction();

    if (this.collection.companyId) {
      this.selectedCompanyId = this.collection.companyId;
    }

    const customInfo = this.parseCustomInfo(this.collection.customInfo);
    this.customUrl = customInfo.url || '';
    this.seoTitle = customInfo.seo?.seoTitle || '';
    this.seoDescription = customInfo.seo?.seoDescription || '';
    this.seoKeywords = customInfo.seo?.seoKeywords || '';
  },
};
</script>

<template>
  <div :class="b('')">
    <div :class="(b('top-buttons'))">
      <a-button
          :class="b('top-btn')"
          @click="goBack(null)"
      >
        Go back
      </a-button>
    </div>
    <div :class="b('main-block')">
      <div :class="b('collection-block')">
        <div :class="b('collection-text')">
          <div :class="b('collection-text-label')">Name:</div>
          <a-input
              type="text"
              v-model:value="collection.name"
          />
        </div>
        <div :class="b('collection-text')">
          <div :class="b('collection-text-label')">Company:</div>
          <a-select
              v-model:value="selectedCompanyId"
              :options="companiesOptions"
              placeholder="Select a company"
              style="width: 100%"
              allow-clear
          />
        </div>
        <div :class="b('collection-text')">
          <div :class="b('collection-text-label')">Title:</div>
          <a-input
              type="text"
              v-model:value="collection.title"
          />
        </div>
        <div :class="b('collection-text')">
          <div :class="b('collection-text-label')">Description:</div>
          <a-textarea
              v-model:value="collection.description"
              :auto-size="{ minRows: 5, maxRows: 5 }"
          />
        </div>
        <div :class="b('collection-text')">
          <div :class="b('collection-text-label')">URL:</div>
          <a-input
              type="text"
              v-model:value="customUrl"
              placeholder="Custom URL for collection"
          />
        </div>
        <div :class="b('collection-text')">
          <div :class="b('collection-text-label')">SEO Title:</div>
          <a-input
              type="text"
              v-model:value="seoTitle"
              placeholder="SEO Title"
          />
        </div>
        <div :class="b('collection-text')">
          <div :class="b('collection-text-label')">SEO Description:</div>
          <a-textarea
              v-model:value="seoDescription"
              :auto-size="{ minRows: 3, maxRows: 5 }"
              placeholder="SEO Description"
          />
        </div>
        <div :class="b('collection-text')">
          <div :class="b('collection-text-label')">SEO Keywords:</div>
          <a-input
              type="text"
              v-model:value="seoKeywords"
              placeholder="SEO Keywords (comma separated)"
          />
        </div>
      </div>
      <img :class="b('collection-image')" :src="collection.previewImage" alt="collection preview">
      <div :class="b('main-buttons')">
        <a-button
            :class="b('main-button')"
            type="primary"
            @click="updateCollection"
        >
          Save Collection
        </a-button>
        <a-button
            :class="b('main-button')"
            type="primary"
            @click="framesToggle"
        >
          Add frames
        </a-button>
        <div :class="b('upload-button')">
          <a-upload
              :class="b('upload-btn')"
              :custom-request="handleChangeImage"
              :before-upload="beforeUpload"
          >
            <a-button>
              Change image
            </a-button>
          </a-upload>
        </div>
      </div>
    </div>
    <div :class="b('frames-block')">
      <div :class="b('frame-block-title')">Selected frames</div>
      <div :class="b('frame-wrapper')">
        <button
            :class="b('frame')"
            v-for="(product, index) in collection.products"
            :key="product.id"
            :style="{order: product.order}"
        >
          <span :class="b('order-buttons')">
          <span :class="b('button', { left: true })" v-if="index > 0">
            <a-button
                type="primary"
                size="small"
                :class="b('arrow')"
                @click.stop="updateProductOrder([
                    {id: product.id, index: index - 1},
                    {id: collection.products[index - 1].id, index},
                ])"
            >
              <template #icon>
                <LeftOutlined />
              </template>
            </a-button>
          </span>
          <span
              :class="b('button', { right: true })"
              v-if="index !== collection.products.length - 1"
          >
            <a-button
                type="primary"
                size="small"
                :class="b('arrow')"
                @click.stop="updateProductOrder([
                    {id: product.id, index: index + 1},
                    {id: collection.products[index + 1].id, index},
                ])"
            >
              <template #icon>
                <RightOutlined />
              </template>
            </a-button>
          </span>
        </span>
        <span :class="b('frame-content')">
          <span :class="b('frame-title')">
            {{ product.displayName }}
          </span>
          <span :class="b('controls')">
            <a-button
                :class="b('btn')"
                type="primary"
                size="small"
                @click="selectFrame(product)"
            >
              Edit
            </a-button>
            <a-popconfirm
                :class="b('btn')"
                title="Sure to delete?"
                @confirm="deleteFrame(product)"
            >
              <a-button
                  type="primary"
                  size="small"
                  html-type="button"
                  danger
              >
                Delete
              </a-button>
            </a-popconfirm>
          </span>
          <img :class="b('frame-image')" :src="product.imageUri" :alt="product.name" />
        </span>
        </button>
      </div>
    </div>
    <CollectionFrameOptions
        v-if="isShowFrameEditModal"
        :show="isShowFrameEditModal"
        :frame="selectedFrame"
        :selected-options="selectedOptions"
        :hide-modal="frameEditToggle"
        :selected-collection="selectedCollection"
    />
    <NewFrameInCollection
        v-if="isShowFramesModal"
        :show="isShowFramesModal"
        :hide-modal="framesClose"
    />
  </div>
</template>

<style scoped lang="scss" src="./pageCollectionStyle.scss"></style>
